// Expert Status Service
// Activity-based expert leveling system with health tracking

import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import {
  ExpertStatus,
  ExpertStatusLevel,
  ExpertStatusHealth,
  EXPERT_STATUS_LEVELS
} from '../types/expertStatus.types';

/**
 * Get user's expert status
 */
export async function getExpertStatus(userId: string): Promise<ExpertStatus | null> {
  const ref = doc(db, 'expertStatus', userId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) {
    return null;
  }
  
  const data = snap.data();
  return {
    userId,
    level: data.level ?? 'none',
    conversationsLast30Days: data.conversationsLast30Days ?? 0,
    lastConversationDate: data.lastConversationDate?.toDate() ?? null,
    lastLoginDate: data.lastLoginDate?.toDate() ?? new Date(),
    loginStreak: data.loginStreak ?? 0,
    health: data.health ?? 100,
    healthStatus: data.healthStatus ?? 'healthy',
    daysUntilDowngrade: data.daysUntilDowngrade ?? null,
    aiVisibility: data.aiVisibility ?? {
      enabled: false,
      publicProfileEnabled: false,
      searchRanking: 0,
      lastIndexed: null
    },
    slug: data.slug ?? null,
    totalConversations: data.totalConversations ?? 0,
    levelAchievedAt: data.levelAchievedAt ?? {
      verified: null,
      expert: null,
      top_expert: null
    },
    referralBoost: data.referralBoost ?? 0,
    activeReferrals: data.activeReferrals ?? 0,
    createdAt: data.createdAt?.toDate() ?? new Date(),
    updatedAt: data.updatedAt?.toDate() ?? new Date()
  };
}

/**
 * Initialize expert status for new user
 */
export async function initializeExpertStatus(userId: string): Promise<void> {
  const ref = doc(db, 'expertStatus', userId);
  await setDoc(ref, {
    level: 'none',
    conversationsLast30Days: 0,
    lastConversationDate: null,
    lastLoginDate: serverTimestamp(),
    loginStreak: 1,
    health: 100,
    healthStatus: 'healthy',
    daysUntilDowngrade: null,
    aiVisibility: {
      enabled: false,
      publicProfileEnabled: false,
      searchRanking: 0,
      lastIndexed: null
    },
    slug: null,
    totalConversations: 0,
    levelAchievedAt: {
      verified: null,
      expert: null,
      top_expert: null
    },
    referralBoost: 0,
    activeReferrals: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

/**
 * Track login (updates streak and last login)
 */
export async function trackLogin(userId: string): Promise<void> {
  const status = await getExpertStatus(userId);
  
  if (!status) {
    await initializeExpertStatus(userId);
    return;
  }
  
  const now = new Date();
  const lastLogin = status.lastLoginDate;
  const daysSinceLastLogin = Math.floor(
    (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24)
  );
  
  let newStreak = status.loginStreak;
  
  if (daysSinceLastLogin === 0) {
    // Same day, no change
    return;
  } else if (daysSinceLastLogin === 1) {
    // Consecutive day, increment streak
    newStreak++;
  } else {
    // Streak broken, reset
    newStreak = 1;
  }
  
  await updateDoc(doc(db, 'expertStatus', userId), {
    lastLoginDate: serverTimestamp(),
    loginStreak: newStreak,
    updatedAt: serverTimestamp()
  });
}

/**
 * Track conversation completion (updates conversation count)
 */
export async function trackConversation(userId: string): Promise<void> {
  const status = await getExpertStatus(userId);
  
  if (!status) {
    await initializeExpertStatus(userId);
  }
  
  // Get conversations in last 30 days
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const connectionsQ = query(
    collection(db, 'connections'),
    where('participants', 'array-contains', userId),
    where('phaseStatus', '==', 'done')
  );
  
  const snap = await getDocs(connectionsQ);
  const recentConversations = snap.docs.filter(doc => {
    const completedAt = doc.data().completedAt?.toDate();
    return completedAt && completedAt >= thirtyDaysAgo;
  });
  
  await updateDoc(doc(db, 'expertStatus', userId), {
    conversationsLast30Days: recentConversations.length,
    lastConversationDate: serverTimestamp(),
    totalConversations: (status?.totalConversations ?? 0) + 1,
    updatedAt: serverTimestamp()
  });
  
  // Recalculate level and health
  await recalculateStatus(userId);
}

/**
 * Recalculate expert level and health based on activity
 */
export async function recalculateStatus(userId: string): Promise<void> {
  const status = await getExpertStatus(userId);
  if (!status) return;
  
  const now = new Date();
  const daysSinceLastLogin = status.lastLoginDate
    ? Math.floor((now.getTime() - status.lastLoginDate.getTime()) / (1000 * 60 * 60 * 24))
    : 999;
  
  // Calculate target level based on activity
  const targetLevel = calculateLevel(status.conversationsLast30Days, daysSinceLastLogin);
  
  // Calculate health score
  const health = calculateHealth(status.conversationsLast30Days, daysSinceLastLogin, status.level);
  const healthStatus = getHealthStatus(health);
  const daysUntilDowngrade = calculateDaysUntilDowngrade(status.level, daysSinceLastLogin);
  
  // Check if level changed
  const levelChanged = targetLevel !== status.level;
  const levelAchievedAt = { ...status.levelAchievedAt };
  
  if (levelChanged && targetLevel !== 'none') {
    levelAchievedAt[targetLevel] = new Date();
  }
  
  // Update search ranking
  const searchRanking = calculateSearchRanking(
    targetLevel,
    health,
    status.conversationsLast30Days,
    status.referralBoost,
    status.totalConversations
  );
  
  // AI visibility enabled for verified+ with healthy status
  const aiVisibilityEnabled = targetLevel !== 'none' && health >= 70;
  
  await updateDoc(doc(db, 'expertStatus', userId), {
    level: targetLevel,
    health,
    healthStatus,
    daysUntilDowngrade,
    levelAchievedAt,
    aiVisibility: {
      ...status.aiVisibility,
      enabled: aiVisibilityEnabled,
      searchRanking
    },
    updatedAt: serverTimestamp()
  });
}

/**
 * Calculate expert level based on activity
 */
function calculateLevel(conversationsLast30Days: number, daysSinceLastLogin: number): ExpertStatusLevel {
  // Check from highest to lowest
  const levels: ExpertStatusLevel[] = ['top_expert', 'expert', 'verified', 'none'];
  
  for (const level of levels) {
    const requirements = EXPERT_STATUS_LEVELS[level];
    if (
      conversationsLast30Days >= requirements.conversationsRequired &&
      daysSinceLastLogin <= requirements.maxDaysSinceLogin
    ) {
      return level;
    }
  }
  
  return 'none';
}

/**
 * Calculate health score (0-100)
 */
function calculateHealth(
  conversationsLast30Days: number,
  daysSinceLastLogin: number,
  currentLevel: ExpertStatusLevel
): number {
  const requirements = EXPERT_STATUS_LEVELS[currentLevel];
  
  // Base health from conversations
  const conversationScore = Math.min(
    (conversationsLast30Days / requirements.conversationsRequired) * 60,
    60
  );
  
  // Recency score from login activity
  const recencyScore = Math.max(
    40 - (daysSinceLastLogin / requirements.maxDaysSinceLogin) * 40,
    0
  );
  
  return Math.round(Math.min(conversationScore + recencyScore, 100));
}

/**
 * Get health status label
 */
function getHealthStatus(health: number): ExpertStatusHealth {
  if (health >= 70) return 'healthy';
  if (health >= 50) return 'warning';
  if (health >= 30) return 'urgent';
  return 'expiring';
}

/**
 * Calculate days until downgrade
 */
function calculateDaysUntilDowngrade(
  currentLevel: ExpertStatusLevel,
  daysSinceLastLogin: number
): number | null {
  if (currentLevel === 'none') return null;
  
  const requirements = EXPERT_STATUS_LEVELS[currentLevel];
  const remainingDays = requirements.maxDaysSinceLogin - daysSinceLastLogin;
  
  return remainingDays > 0 ? remainingDays : 0;
}

/**
 * Calculate search ranking for AI indexing
 */
function calculateSearchRanking(
  level: ExpertStatusLevel,
  health: number,
  conversationsLast30Days: number,
  referralBoost: number,
  totalConversations: number
): number {
  // Base score from level
  const levelScores: Record<ExpertStatusLevel, number> = {
    none: 0,
    verified: 50,
    expert: 75,
    top_expert: 100
  };
  
  const baseScore = levelScores[level];
  
  // Activity multiplier
  const activityMultiplier = Math.min(conversationsLast30Days * 0.1, 1);
  
  // Health penalty
  const healthMultiplier = health / 100;
  
  // Experience bonus
  const experienceBonus = Math.min(totalConversations * 0.5, 20);
  
  // Total ranking
  const ranking = Math.round(
    (baseScore * activityMultiplier * healthMultiplier) + 
    referralBoost + 
    experienceBonus
  );
  
  return Math.max(0, Math.min(ranking, 200)); // Cap at 200
}

/**
 * Enable AI visibility (opt-in)
 */
export async function enableAIVisibility(userId: string, enabled: boolean): Promise<void> {
  const status = await getExpertStatus(userId);
  if (!status) return;
  
  await updateDoc(doc(db, 'expertStatus', userId), {
    'aiVisibility.enabled': enabled,
    'aiVisibility.lastIndexed': enabled ? serverTimestamp() : null,
    updatedAt: serverTimestamp()
  });
}

/**
 * Set public profile slug
 */
export async function setProfileSlug(userId: string, slug: string): Promise<boolean> {
  // Check if slug is available
  const existingQ = query(
    collection(db, 'expertStatus'),
    where('slug', '==', slug)
  );
  const existingSnap = await getDocs(existingQ);
  
  if (!existingSnap.empty && existingSnap.docs[0].id !== userId) {
    return false; // Slug taken
  }
  
  await updateDoc(doc(db, 'expertStatus', userId), {
    slug,
    updatedAt: serverTimestamp()
  });
  
  return true;
}

/**
 * Get next milestone for motivation
 */
export function calculateNextMilestone(status: ExpertStatus): {
  level: ExpertStatusLevel;
  conversationsNeeded: number;
  daysToMaintain: number;
} | null {
  const currentLevelIndex = ['none', 'verified', 'expert', 'top_expert'].indexOf(status.level);
  const nextLevel = (['verified', 'expert', 'top_expert', null] as const)[currentLevelIndex];
  
  if (!nextLevel) return null;
  
  const requirements = EXPERT_STATUS_LEVELS[nextLevel];
  const conversationsNeeded = Math.max(0, requirements.conversationsRequired - status.conversationsLast30Days);
  
  return {
    level: nextLevel,
    conversationsNeeded,
    daysToMaintain: requirements.maxDaysSinceLogin
  };
}
